/**
 * @fileoverview ScrollingNav - Navigation with scroll-triggered logo transition
 *
 * Purpose: Navbar that shrinks on scroll with smooth logo morph animation
 * Features:
 * - Detects scroll position and applies "scrolled" class after threshold
 * - Logo transitions from full wordmark to compact icon
 * - Header height reduces for fixed sticky behavior
 * - Smooth CSS transitions for professional feel
 * - Demo request dialog for unauthenticated users
 *
 * Used by: Homepage, public pages
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Logo } from "@/ui/layout";
import { Button, buttonVariants } from "@/ui/primitives";
import { cn } from "@/utils/misc";
import { DemoRequestDialog } from "./-ui.demo-request-dialog";

interface ScrollingNavProps {
  isAuthenticated?: boolean;
  isLoading?: boolean;
  loginPath: string;
  dashboardPath: string;
}

/**
 * Navigation bar with scroll-triggered logo transition
 *
 * @param isAuthenticated - Whether user is logged in
 * @param isLoading - Loading state for auth (reserved for future use)
 * @param loginPath - Path to login page
 * @param dashboardPath - Path to dashboard
 */
export function ScrollingNav({
  isAuthenticated,
  isLoading: _isLoading,
  loginPath,
  dashboardPath,
}: ScrollingNavProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);

  const handleScroll = useCallback(() => {
    const scrollThreshold = 50;
    setIsScrolled(window.scrollY > scrollThreshold);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    // Check initial scroll position
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <>
      <div
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-500 ease-in-out",
          // Transparent initially, glassmorphism on scroll
          isScrolled
            ? "border-b border-border/20 bg-background/55 shadow-sm backdrop-blur-sm"
            : "bg-transparent",
        )}
      >
        <nav
          className={cn(
            "mx-auto flex w-full max-w-7xl items-center justify-between px-6 transition-all duration-300 ease-in-out",
            isScrolled ? "py-2" : "py-2",
          )}
        >
          {/* Logo - responsive: compact on mobile, full on tablet+ */}
          <Link to="/" className="flex items-center">
            {/* Mobile: Icon only */}
            <Logo
              compact
              className={cn(
                "md:hidden",
                isScrolled ? "text-primary" : "text-white",
              )}
            />
            {/* Tablet and up: Full logo with wordmark */}
            <Logo
              className={cn(
                "hidden md:block",
                isScrolled ? "text-primary" : "text-white",
              )}
            />
          </Link>

          {/* Navigation links */}
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link
                to={dashboardPath}
                className={cn(
                  buttonVariants({ variant: "outline", size: "sm" }),
                  "border-primary bg-white text-[hsl(140,4%,14%)] hover:bg-white/90 dark:border-primary dark:bg-white dark:text-[hsl(140,4%,14%)] dark:hover:bg-white/90",
                )}
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to={loginPath}
                  className={cn(
                    buttonVariants({ variant: "link", size: "sm" }),
                    "px-0 hover:no-underline",
                    isScrolled
                      ? "text-primary/80 hover:text-accent"
                      : "text-white/90 hover:text-accent",
                  )}
                >
                  Login
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-primary bg-white text-[hsl(140,4%,14%)] hover:bg-white/90 dark:border-primary dark:bg-white dark:text-[hsl(140,4%,14%)] dark:hover:bg-white/90"
                  onClick={() => setIsDemoDialogOpen(true)}
                >
                  Book a demo
                </Button>
              </>
            )}
          </div>
        </nav>
      </div>

      <DemoRequestDialog
        open={isDemoDialogOpen}
        onOpenChange={setIsDemoDialogOpen}
      />
    </>
  );
}
